import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import { getAuth, type Auth, connectAuthEmulator } from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
  connectFirestoreEmulator,
} from 'firebase/firestore';
import { getFunctions, type Functions, connectFunctionsEmulator } from 'firebase/functions';
import {
  getStorage,
  type FirebaseStorage,
  connectStorageEmulator,
} from 'firebase/storage';

/**
 * Firebase web config. These values are public — Firebase embeds them in
 * any web app build, and the API key is scoped via Firebase Security Rules,
 * not via secrecy. We hardcode the production values as a fallback so the
 * app boots even when `apps/web/.env.local` isn't present.
 */
const PROD_CONFIG = {
  apiKey: 'AIzaSyDgX1a5qNUG0rEKhgyuO2s0EvU6a8In-4o',
  authDomain: 'cp-3-e5e2a.firebaseapp.com',
  projectId: 'cp-3-e5e2a',
  storageBucket: 'cp-3-e5e2a.firebasestorage.app',
  messagingSenderId: '1019188911793',
  appId: '1:1019188911793:web:83612bbfe77f77f5618c90',
} as const;

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? PROD_CONFIG.apiKey,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? PROD_CONFIG.authDomain,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? PROD_CONFIG.projectId,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? PROD_CONFIG.storageBucket,
  messagingSenderId: import.meta.env.VITE_FIREBASE_SENDER_ID ?? PROD_CONFIG.messagingSenderId,
  appId: import.meta.env.VITE_FIREBASE_APP_ID ?? PROD_CONFIG.appId,
};

export const app: FirebaseApp = initializeApp(config);
export const auth: Auth = getAuth(app);
export const firestore: Firestore = initializeFirestore(app, {
  ignoreUndefinedProperties: true,
  // IndexedDB-backed cache so reads stay available offline and warm
  // refetches don't hammer Firestore. The multi-tab manager keeps
  // separate browser tabs in sync without each fetching independently.
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
});
export const functions: Functions = getFunctions(app, 'europe-west3');
export const storage: FirebaseStorage = getStorage(app);

if (import.meta.env.VITE_USE_EMULATORS === '1') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(firestore, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
}

/**
 * Optional App Check — protects every callable / Firestore / Storage
 * request against scripted abuse.
 *
 * Enables itself only when `VITE_APP_CHECK_RECAPTCHA_SITE_KEY` is set
 * in `.env.local` (or the production env). The key comes from a
 * reCAPTCHA Enterprise site key registered in the GCP console for
 * `cp-3.web.app`. Once the env var is present, also flip the
 * "Enforce App Check" switches in the Firebase console for Functions
 * (callables), Firestore, and Storage.
 *
 * Skipped in the emulator environment — App Check has no emulator and
 * the SDK throws if asked to bootstrap against `localhost`.
 */
const appCheckSiteKey = import.meta.env.VITE_APP_CHECK_RECAPTCHA_SITE_KEY as string | undefined;
if (appCheckSiteKey && import.meta.env.VITE_USE_EMULATORS !== '1') {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    // Surfacing the error to the console rather than throwing keeps
    // the app boot-able if the site key is misconfigured — Auth still
    // works, App Check just stays off.
    // eslint-disable-next-line no-console
    console.warn('App Check initialisation failed', err);
  }
}
